/* eslint-disable max-lines-per-function */
import React, { useEffect, useState } from "react";
import _ from "lodash";
import moment from "moment";
import { connect } from "react-redux";
import { bindActionCreators, compose } from "redux";
import { withFormik } from "formik";
import numeral from "numeral";
import { InputText, InputTypeAhead } from "@loanbossinc/component-library";
import { Grid } from "@material-ui/core";
import { useFlags } from "launchdarkly-react-client-sdk";
import { convertIntegerToGUID } from "helpers/guid";

import { actionCreators as dyLineItemActionCreators } from "../../debt-yield-line-items-management/debtYieldLineItemManagement.store";
import { actionCreators as dyDashboardActionCreators } from "../../../reportingComponents/debtYieldCalculationDashboard/debtYieldProjectedCalculationDashboard/debtYieldProjectedCalculationDashboard.store";
import { actionCreators as sfManagementActionCreators } from "../../source-file-management/sourceFileManagement.store";
import { PaddedWrapper, PaddedWrapperFullWidth, Row } from "../styles";
import { actionCreators as dashboardActionCreators } from "../../../dashboard/dashboard.store"; // Import the action creators

import { SourceFileListItem } from "./sourceFileListItem";
import SourceFileDataSelection from "./sourceFileDataSelection";
import { actionCreators as sourceFileFieldActionCreators } from "./sourceFileField.store";
import { StyledPrimaryButton, StyledSecondaryButton } from "./sourceFileFieldStyles";
import { financeOverrideTypes } from "../../../../../constants/financeOverrideTypes";

const getFilesForDeal = (props, sourceFileId) => {
  const { allVersionsForDeal, deal, isAdding, isEditing, selectedSourceFile } = props;

  let files = !allVersionsForDeal || allVersionsForDeal.length === 0 ? [] :
    allVersionsForDeal?.filter(v => v.parentId === "" && (v.typeId !== "rent-roll" || v.typeId !== "mf-rent-roll"))
      .map(file => {
        const versions = allVersionsForDeal.filter(f => f.id === file.id || f.parentId === file.id);
        const latestVersion = _.orderBy(versions, v => moment(v.createdAt), "desc")[0];
        let summary = "Deal";
        if (latestVersion.propertyId && latestVersion.propertyId !== "-1") {
          const dealProperty = Boolean(deal) && deal.properties.find(x => x.id === latestVersion.propertyId);
          if (dealProperty) {
            summary = dealProperty.name;
          } else {
            summary = "Property";
          }
        }

        return { ...latestVersion, summary, latestVersionAsOfDate: latestVersion?.asOfDate };
      });

  if (isAdding && selectedSourceFile && files) {
    files = files.filter(x => x.id === selectedSourceFile?.id);
  }

  if (sourceFileId && isEditing && allVersionsForDeal && allVersionsForDeal.length > 0) {
    const versionsForSelectedFileType = allVersionsForDeal.filter(v => v.id === sourceFileId || v.parentId === sourceFileId);

    let latestVersionAsOfDate;
    if (versionsForSelectedFileType.length > 0) {
      latestVersionAsOfDate = _.orderBy(versionsForSelectedFileType, v => moment(v.createdAt), "desc")[0].asOfDate;
    }

    files = allVersionsForDeal
      .filter(v => v.id === sourceFileId)
      .map(file => {
        let summary = "Deal";
        if (file.propertyId && file.propertyId !== "-1") {
          const dealProperty = Boolean(deal) && deal.properties.find(x => x.id === file.propertyId);
          if (dealProperty) {
            summary = dealProperty.name;
          } else {
            summary = "Property";
          }
        }

        return { ...file, summary, latestVersionAsOfDate };
      });
  }

  return files;
};

export const SourceFileField = props => {
  const {
    allDealLoans,
    allVersionsForDeal,
    amountOfCellsSelected,
    deal,
    fieldValue,
    fieldValueToShowAndSave,
    getAllVersions,
    getSourceFilesByDeal,
    getAllLoansForDeal,
    handleSubmit,
    isAdding,
    isFinanceOverride,
    isEditing,
    isFromDebtYieldCalculation,
    isFromFile,
    isSelectingField,
    selectedField,
    selectedSourceFile,
    setDebtYieldCalculationDashboardSelections,
    setFieldSelections,
    setFieldValue,
    setShowLabeledButton,
    setSourceFileFieldSelections,
    setSourceFileFieldToDefault,
    values,
    fieldsFilter,
    isEditingLineItem,
    setDrawerSelections,
    rowsSelected
  } = props;

  const { name = "", loanId: loanIdAsGUID = null, trancheId = null, sourceFileId = null, isAnnualized = true, financeOverrideType = "balance" } = values;
  const loanId = parseInt(loanIdAsGUID, 16);
  const { bar616EnableBalanceOverrideFieldType: enableBalanceOverrideField } = useFlags();
  const [trancheLoanId, setTrancheLoanId] = useState(null);
  const [selectedTranches, setSelectedTranches] = useState([]);

  useEffect(() => {
    if (deal) {
      getSourceFilesByDeal({ dealId: deal.id });
      getAllVersions({ dealId: deal.id });
      if (!allDealLoans) {
        getAllLoansForDeal({ dealId: deal.id }); // Call the action to fetch allDealLoans if not already populated
      }
    }
  }, [deal, allDealLoans, getAllLoansForDeal]);

  useEffect(
    () => () => {
      setSourceFileFieldToDefault();
      setFieldSelections({ selectedField: null });
    },
    []
  );

  useEffect(
    () => {
        if (!allDealLoans || !Array.isArray(allDealLoans)) {
         setTrancheLoanId(null);
         return;
       }
       const matchingLoan = allDealLoans.find(loan => loan.id === loanId);
       setTrancheLoanId(matchingLoan ? matchingLoan.id : null);
    },
    [loanId, allDealLoans]
  );

  useEffect(
    () => {
        if (!allDealLoans || !Array.isArray(allDealLoans)) {
         setSelectedTranches([]);
         return;
       }
       if (!trancheLoanId) {
         setSelectedTranches([]);
         return;
       }
       const tranches = getLoanTranches(trancheLoanId);
       setSelectedTranches(tranches);

       if(tranches?.length === 1) {
          setFieldValue("trancheId", tranches[0].trancheId);
       }
    },
    [trancheLoanId, allDealLoans]
  );

  const getLoanTranches = (id) => {
      const loan = allDealLoans.find(currentLoan => currentLoan.id === id);
      return loan && Array.isArray(loan.tranchesForLoan) ? loan.tranchesForLoan : [];
  };
  const onCancel = () => {
    if (isFromDebtYieldCalculation) {
      setFieldSelections({ currentSelectedFieldType: null, isAdding: false, isEditing: false, selectedField: null });
      setDebtYieldCalculationDashboardSelections({ isLenderReportingDrawerOpen: false, initialDrawerSectionName: null });
    } else {
      setFieldSelections({ currentSelectedFieldType: null, isAdding: false, isEditing: false, selectedField: null });
    }
    if (isFromFile) {
      setShowLabeledButton(false);
    }
    if (isEditingLineItem) {
      setDrawerSelections({ isEditingLineItem: true, currentSectionName: "line-item-management" });
    }
  };

  const onSelectFileOrSelectRange = (sfId, isSelectRange) => {
    if (isSelectRange) {
      setFieldSelections({ isSelectingField: true });
    }
    setFieldValue("sourceFileId", sfId);

    let selectedFile;
    allVersionsForDeal.forEach(file => {
      if (file.id !== sfId && file.parentId !== sfId) {
        return;
      }
      if (!selectedFile) {
        selectedFile = file;

        return;
      }
      if (moment(selectedFile.updatedAt).isBefore(moment(file.updatedAt))) {
        selectedFile = file;
      }
    });

    if (selectedFile) {
      let levelFieldValue;
      if (isFinanceOverride) {
        levelFieldValue = "loan";
      }
      else if (selectedFile.propertyId !== "-1") {
        levelFieldValue = "property";
      }
      else {
        levelFieldValue = "deal";
      }
      setFieldValue("level", levelFieldValue);
      setFieldValue("propertyId", selectedFile.propertyId);
      setFieldValue("isAnnualized", values.isAnnualized === undefined ? true : values.isAnnualized);
      setSourceFileFieldSelections({ selectedSourceFile: selectedFile });
    }
  };

  const onDataSelected = () => {
    setFieldSelections({ isSelectingField: false });
  };

  const onAnnualizeSwitch = () => {
    setFieldValue("isAnnualized", !isAnnualized);
  };

  useEffect(() => {
    const monthsInYear = 12;
    let calculatedValue;
    let rowSelectedLocal = [];
    if((!rowsSelected || rowsSelected.length === 0) && selectedField?.rowsSelected && selectedField.rowsSelected.length > 0){
      rowSelectedLocal = selectedField.rowsSelected;
      setSourceFileFieldSelections({rowsSelected: rowSelectedLocal});
    }
    else if((!rowsSelected || rowsSelected.length === 0) && selectedField) {
        rowSelectedLocal.push({
          dataColumnOffset: selectedField.dataColumnOffset,
          amountOfCellsSelected: selectedField.amountOfCellsSelected,
          fieldValue: selectedField.value
        });
    }
    else {
      rowSelectedLocal = rowsSelected ?? [];
    }
    const totalFieldsValue = rowSelectedLocal?.reduce((acc, curr) => acc + (curr.fieldValue  ?? 0), 0);
    const amountOfCells = rowSelectedLocal?.reduce((acc, curr) => acc + ((curr.amountOfCellsSelected || Math.abs(curr?.dataColumnOffset) + 1) ?? 0), 0) ?? amountOfCellsSelected;
    const totalSelectedValue = totalFieldsValue !== null ? totalFieldsValue : selectedField?.value;
    if (amountOfCells && amountOfCells >= 1) {
      const numOfMonthRows = rowSelectedLocal.length;
      const amountOfMonths = monthsInYear * numOfMonthRows;
      if (isEditing && totalFieldsValue === null) {
        if ((isAnnualized && selectedField.isAnnualized) || (!isAnnualized && !selectedField.isAnnualized)) {
          calculatedValue = totalSelectedValue;
        } else if (isAnnualized && !selectedField.isAnnualized) {
          calculatedValue = (totalSelectedValue / amountOfCells) * amountOfMonths;
        } else {
          calculatedValue = (totalSelectedValue * amountOfCells) / amountOfMonths;
        }
      } else if (isAnnualized) {
        calculatedValue = (totalSelectedValue / amountOfCells) * amountOfMonths;
      } else {
        calculatedValue = totalSelectedValue;
      }
      // this is the total value of the fields sum
      setSourceFileFieldSelections({
                                    fieldValueToShowAndSave: calculatedValue,
                                    amountOfCellsSelected: amountOfCells
      });
    }
  }, [amountOfCellsSelected, selectedField, fieldValue, isAnnualized, rowsSelected]);

  const files = getFilesForDeal(props, sourceFileId);

  const financeOverrideTypeOptions = [];
  for(var k in financeOverrideTypes){
        if(financeOverrideTypes.hasOwnProperty(k)){
      financeOverrideTypeOptions.push({ key: financeOverrideTypes[k], value: k });
    }
  }


  const getDealSourceFileList = () => {
    const filteredFiles = fieldsFilter && fieldsFilter !== "0" ? files.filter(f => f.propertyId === parseInt(fieldsFilter, 10)) : files;

    return _.sortBy(filteredFiles, ["sourceName", "typeName", "summary"]).map(dealSourceFile => (
      <SourceFileListItem
        key={dealSourceFile.id}
        onSelectFileOrSelectRange={onSelectFileOrSelectRange}
        summary={dealSourceFile.summary}
        sourceFileId={dealSourceFile.id}
        sourceName={dealSourceFile.sourceName}
        typeName={dealSourceFile.typeName}
        asOfDate={dealSourceFile.latestVersionAsOfDate}
        updatedAtDate={dealSourceFile.updatedAt}
        onAnnualizeSwitch={onAnnualizeSwitch}
        isAnnualized={isAnnualized}
        calculatedValue={fieldValueToShowAndSave}
        isEditing={isEditing}
        isAdding={isAdding}
        amountOfCellsSelected={amountOfCellsSelected}
        selectedField={selectedField}
        selectedSourceFile={selectedSourceFile}
      />
    ));
  };

  const onNameChange = event => {
    const {
      target: { value: targetValue }
    } = event;

    setFieldValue("name", targetValue);
  };

  const isFinanceOverrideAndIsEnableBalanceOverride = (isFinanceOverrideCondition, enableBalanceOverrideFieldCondition) => isFinanceOverrideCondition && enableBalanceOverrideFieldCondition;

  const onAssociatedLoanIdChange = newLoanId => {
    // Store loanId as a GUID string so parseInt(loanIdAsGUID, 16) correctly
    // recovers the integer ID in the loanId useEffect — storing a raw integer
    // causes parseInt to parse it as hex and mismatch (e.g. 10 → 16)
    setFieldValue("loanId", convertIntegerToGUID(newLoanId));
    // Clear tranche selection when the loan changes
    setFieldValue("trancheId", null);
    setTrancheLoanId(newLoanId);
  };

  const onAssociatedTrancheIdChange = newTrancheId => {
    setFieldValue("trancheId", newTrancheId);
  };
const onFinanceOverrideTypeChange = overridType => {
    setFieldValue("financeOverrideType", overridType);
  };
  const isFormValid = Boolean(name) && fieldValueToShowAndSave !== null;

  if (!isSelectingField) {
    return (
      <>
        <Row justifyContent="flex-start">
          <PaddedWrapperFullWidth padding="1em">
            <InputText variant="filled" label="Field Name" name="field-name" fullWidth onChange={onNameChange} value={name} />
          </PaddedWrapperFullWidth>
        </Row>

        {
          isFinanceOverrideAndIsEnableBalanceOverride(isFinanceOverride, enableBalanceOverrideField) &&
          <Row fullWidth>
          <PaddedWrapperFullWidth padding="1em">
            <InputTypeAhead
              variant="filled"
              id="financeOverrideType"
              name="financeOverrideType"
              label="Override type"
              options={financeOverrideTypeOptions}
              displayWith={type => type.key}
              setValueWith={type => type.value}
              value={financeOverrideTypeOptions.find(type => type.value === financeOverrideType)}
              onChange={onFinanceOverrideTypeChange}
            />
          </PaddedWrapperFullWidth>
        </Row>
        }

        {allVersionsForDeal && Boolean(allVersionsForDeal.length) && getDealSourceFileList()}

        {
            isFinanceOverrideAndIsEnableBalanceOverride(isFinanceOverride, enableBalanceOverrideField) &&
            allDealLoans &&
            <Row fullWidth>
              <PaddedWrapperFullWidth padding="1em">
                <InputTypeAhead
                  variant="filled"
                  id="loanId"
                  name="loanId"
                  label="Choose a Loan"
                  options={allDealLoans}
                  displayWith={loan => loan && loan.name}
                  setValueWith={loan => loan && loan.id}
                  value={allDealLoans.find(loan => loan.id === loanId)}
                  onChange={onAssociatedLoanIdChange}
                />
              </PaddedWrapperFullWidth>
            </Row>
        }

        {
            isFinanceOverrideAndIsEnableBalanceOverride(isFinanceOverride, enableBalanceOverrideField) &&
           selectedTranches?.length > 1 &&
            <Row fullWidth>
              <PaddedWrapperFullWidth padding="1em">
                <InputTypeAhead
                  variant="filled"
                  id="trancheId"
                  name="trancheId"
                  label="Choose a Tranche"
                  options={selectedTranches}
                  displayWith={tranche => tranche && tranche.trancheName}
                  setValueWith={tranche => tranche && tranche.trancheId}
                  value={selectedTranches.find(tranche => tranche.trancheId === trancheId) ?? null}
                  onChange={onAssociatedTrancheIdChange}
                />
              </PaddedWrapperFullWidth>
            </Row>
        }

        {(isEditing || (isAdding && selectedSourceFile)) && (
          <Row borderBottom="none" justifyContent="flex-end">
            <PaddedWrapper>
              <Grid container>
                <Grid item>
                  <StyledSecondaryButton size="sm" label="Cancel" onClick={onCancel} />
                </Grid>
                <Grid item>
                  <PaddedWrapper padding="0 1em">
                    <StyledPrimaryButton size="sm" label={isFromDebtYieldCalculation ? "Done" : "Save"} disabled={!isFormValid} onClick={handleSubmit} />
                  </PaddedWrapper>
                </Grid>
              </Grid>
            </PaddedWrapper>
          </Row>
        )}
      </>
    );
  }

  return <SourceFileDataSelection {...props} onDataSelected={onDataSelected} />;
};

const mapStateToProps = ({ lenderReportSourceFileField, lenderReportSourceFileManagement: { allVersionsForDeal }, lenderReportList: { allDealLoans } }) => ({
  ...lenderReportSourceFileField,
  allVersionsForDeal,
  allDealLoans // Map allDealLoans to props
});

const mapDispatchToProps = dispatch => bindActionCreators(
  {
    ...sourceFileFieldActionCreators,
    ...dyLineItemActionCreators,
    ...dyDashboardActionCreators,
    ...sfManagementActionCreators,
    ...dashboardActionCreators
  },
  dispatch
);

const convertToGuidIfNumber = value => isNaN(Number(value)) ? value : convertIntegerToGUID(value);

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  withFormik({
    mapPropsToValues: ({ selectedField }) => ({ ...selectedField, rowsSelected: selectedField?.rowsSelected ?? [] }),
    handleSubmit(values, { props, setSubmitting }) {
      const {
        dealId,
        isAdding,
        postFieldConfig,
        putFieldConfig,
        setFieldSelections,
        changeDrawerStateIfInNewListItemMode,
        isFinanceOverride,
        isFromDebtYieldCalculation,
        selectedSourceFile,
        setDYLineItemSelections,
        setDebtYieldCalculationDashboardSelections,
        rowAnchor1ColumnIndex,
        rowAnchor1Value,
        rowAnchor2ColumnIndex,
        rowAnchor2Value,
        dataColumnAnchorIndex,
        selectedField,
        dataColumnOffset,
        fieldValueToShowAndSave,
        isEditingLineItem,
        setNoiCalculationsDashboardSelections,
        setDrawerSelections,
        setNewLineItemsSelectionsToDefault,
        rowsSelected

      } = props;

      const loanIDAsGUID = values.loanId ? convertToGuidIfNumber(values.loanId) : undefined;

      const payload = {
        ...values,
        isFromFile: !isFinanceOverride,
        isFinanceOverride,
        type: isFinanceOverride ? "finance_override" : "file",
        dealId,
        loanId: loanIDAsGUID,
        value: fieldValueToShowAndSave,
        formattedValue: numeral(fieldValueToShowAndSave).format("0.00"),
        dataColumnAnchorIndex: dataColumnAnchorIndex !== null ? dataColumnAnchorIndex : selectedField?.dataColumnAnchorIndex,
        dataColumnOffset: dataColumnOffset !== null ? dataColumnOffset : selectedField?.dataColumnOffset || 0,
        rowAnchor1ColumnIndex: rowAnchor1ColumnIndex !== null ? rowAnchor1ColumnIndex : selectedField?.rowAnchor1ColumnIndex,
        rowAnchor1Value: rowAnchor1Value !== null ? rowAnchor1Value : selectedField?.rowAnchor1Value,
        rowAnchor2ColumnIndex: rowAnchor2ColumnIndex !== null ? rowAnchor2ColumnIndex : selectedField?.rowAnchor2ColumnIndex,
        rowAnchor2Value: rowAnchor1Value !== null ? rowAnchor2Value : selectedField?.rowAnchor2Value,
        rowsSelected
      };

      if (isAdding) {
        postFieldConfig({ data: payload });
        if (isFromDebtYieldCalculation) {
          setDYLineItemSelections({ sourceFileLineItem: { selectedSourceFile, fieldValueToShowAndSave } });
          setDebtYieldCalculationDashboardSelections({ isLenderReportingDrawerOpen: false, initialDrawerSectionName: null });
        }
      } else {
        putFieldConfig({ data: _.omit(payload, ["updatedAt", "createdAt"]) });
      }

      setSubmitting(false);
      changeDrawerStateIfInNewListItemMode();
      setFieldSelections({ currentSelectedFieldType: null, selectedField: null, isAdding: false, isEditing: false });
      if (isEditingLineItem) {
        setNoiCalculationsDashboardSelections({
          initialDrawerSectionName: null,
          isLenderReportingDrawerOpen: false,
          addLineItemType: null,
          lineItemToEdit: null
        });
        setDrawerSelections({ isEditingLineItem: false, currentSectionName: null });
        setNewLineItemsSelectionsToDefault();
      }
    }
  })
)(SourceFileField);
